import type { PMPartsAndProcedure, PMServiceLevel, ServiceLevelAssignments } from "../types/pmTypes";
import { isProcedureType } from "./companyResourceUtils";
import { pmIntervalNestsInto } from "./pmUtils";

export type InheritedMatrixAssignment = {
    quantity: number;
    sourceLevelIdentifiers: string[];
};

type MatrixNestLevel = {
    id: string;
    intervalValue?: number | null;
    levelIdentifier?: string | null;
};

/**
 * When a higher PM level nests a lower one (evenly divisible interval), matrix
 * cells for items assigned on the lower level are shown as read-only autofill
 * on the higher column. Returns null when nothing is inherited.
 */
export function getInheritedMatrixAssignment(
    targetLevel: MatrixNestLevel,
    partId: string,
    allLevels: MatrixNestLevel[],
    assignments: Record<string, Record<string, boolean | number>>,
): InheritedMatrixAssignment | null {
    const targetInterval = Number(targetLevel.intervalValue) || 0;
    if (!(targetInterval > 0) || !partId) return null;

    let maxQty = 0;
    const sources: Array<{ interval: number; label: string }> = [];

    for (const level of allLevels) {
        if (level.id === targetLevel.id) continue;
        const interval = Number(level.intervalValue) || 0;
        if (!(interval > 0) || interval >= targetInterval) continue;
        if (!pmIntervalNestsInto(targetInterval, interval)) continue;

        const qty = getAssignmentQuantity(assignments[level.id]?.[partId]);
        if (qty <= 0) continue;

        maxQty = Math.max(maxQty, qty);
        sources.push({
            interval,
            label: String(level.levelIdentifier || level.id),
        });
    }

    if (maxQty <= 0) return null;

    sources.sort((a, b) => a.interval - b.interval || a.label.localeCompare(b.label));
    const sourceLevelIdentifiers = [
        ...new Set(sources.map((source) => source.label)),
    ];

    return { quantity: maxQty, sourceLevelIdentifiers };
}

// ----- Parts Resolution (Req 4.5, 9.5) -----

/**
 * Quantity (or 1 for a checked procedure) if the item is assigned to the level; otherwise 0.
 * Consumables are stored as numbers in the Service Matrix; procedures/legacy rows use `true`.
 */
export function getAssignmentQuantity(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    if (value === true) return 1;
    return 0;
}

export function parseServiceLevelAssignments(raw: unknown): ServiceLevelAssignments {
    if (raw == null || raw === '') return {};

    let value: unknown = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return {};
        }
    }
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return {};
        }
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return value as ServiceLevelAssignments;
}

const MATRIX_QUANTITY_DRAFT = /^\d*\.?\d*$/;

/**
 * Accepts a service-matrix quantity while the user is still typing, including
 * fractional gallons such as "2." and "2.75". Rejects anything that is not a
 * non-negative decimal draft.
 */
export function parseMatrixQuantityDraft(raw: string): { accepted: boolean; quantity: number } {
    if (!MATRIX_QUANTITY_DRAFT.test(raw)) return { accepted: false, quantity: 0 };
    if (raw === '' || raw === '.') return { accepted: true, quantity: 0 };
    const normalized = raw.endsWith('.') ? raw.slice(0, -1) : raw;
    const quantity = Number(normalized);
    if (!Number.isFinite(quantity) || quantity < 0) return { accepted: false, quantity: 0 };
    return { accepted: true, quantity };
}

type MatrixLevelSource = { id: string; serviceLevelAssignments?: unknown };

export function assignmentsFromServiceLevels(
    serviceLevels: MatrixLevelSource[],
): Record<string, Record<string, boolean | number>> {
    const parsed: Record<string, Record<string, boolean | number>> = {};
    for (const sl of serviceLevels) {
        parsed[sl.id] = parseServiceLevelAssignments(sl.serviceLevelAssignments);
    }
    return parsed;
}

/**
 * Keeps in-progress level quantities when the parts list reloads.
 * New levels pick up saved assignments; removed levels drop out.
 */
export function retainUnsavedMatrixAssignments(
    serviceLevels: MatrixLevelSource[],
    previous: Record<string, Record<string, boolean | number>>,
): Record<string, Record<string, boolean | number>> {
    const fromServer = assignmentsFromServiceLevels(serviceLevels);
    const next: Record<string, Record<string, boolean | number>> = {};
    for (const sl of serviceLevels) {
        next[sl.id] = previous[sl.id] ?? fromServer[sl.id];
    }
    return next;
}

/**
 * Reloading service levels must not wipe quantities already on screen.
 * Unsaved edits win. A refresh that comes back without assignment data keeps
 * the quantities that were already showing.
 */
export function mergeMatrixAssignmentsOnRefresh(
    serviceLevels: MatrixLevelSource[],
    previous: Record<string, Record<string, boolean | number>>,
    dirty: boolean,
): Record<string, Record<string, boolean | number>> {
    if (dirty) return retainUnsavedMatrixAssignments(serviceLevels, previous);

    const fromServer = assignmentsFromServiceLevels(serviceLevels);
    const next: Record<string, Record<string, boolean | number>> = {};
    for (const sl of serviceLevels) {
        const server = fromServer[sl.id] ?? {};
        const local = previous[sl.id];
        const keepLocal = Object.keys(server).length === 0 && !!local && Object.keys(local).length > 0;
        next[sl.id] = keepLocal ? local : server;
    }
    return next;
}

type MatrixRow = {
    id: string;
    itemType?: string | null;
    sortOrder?: number | null;
};

/**
 * Service items first, then procedures. Within each group, lower sortOrder first.
 * A missing sortOrder counts as 0 inside its group, so a new procedure cannot
 * land between inventory lines.
 */
export function sortMatrixRows<T extends MatrixRow>(rows: T[]): T[] {
    return [...rows].sort((a, b) => {
        const group = Number(isProcedureType(a.itemType)) - Number(isProcedureType(b.itemType));
        if (group !== 0) return group;
        const order = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        if (order !== 0) return order;
        return a.id.localeCompare(b.id);
    });
}

/**
 * Pulls procedures out of the service items after a drag, keeping the relative
 * order inside each group from the list as dropped.
 */
export function partitionMatrixRows<T extends { itemType?: string | null }>(rows: T[]): T[] {
    const items: T[] = [];
    const procedures: T[] = [];
    for (const row of rows) {
        if (isProcedureType(row.itemType)) procedures.push(row);
        else items.push(row);
    }
    return [...items, ...procedures];
}

/** Next sortOrder at the end of the matching group. An empty group starts at 0. */
export function nextMatrixGroupSortOrder(
    rows: { itemType?: string | null; sortOrder?: number | null }[],
    itemType?: string | null,
): number {
    const incomingIsProcedure = isProcedureType(itemType);
    const group = rows.filter((row) => isProcedureType(row.itemType) === incomingIsProcedure);
    return group.reduce((max, row) => Math.max(max, row.sortOrder ?? 0), -1) + 1;
}

export function isAssignedToServiceLevel(value: unknown): boolean {
    return getAssignmentQuantity(value) > 0;
}

/**
 * Resolves the service level assignment matrix to a concrete list of parts
 * required for a given service level.
 *
 * Returns parts assigned with `true` (procedures) or a quantity greater than 0 (consumables).
 *
 * Validates: Requirements 4.5, 9.5
 */
export function getPartsForServiceLevel(
    parts: PMPartsAndProcedure[],
    serviceLevelAssignments: ServiceLevelAssignments,
): PMPartsAndProcedure[] {
    return parts.filter((part) => isAssignedToServiceLevel(serviceLevelAssignments[part.id]));
}

/**
 * Builds a level-id → assignments map for nesting lookups.
 */
export function buildServiceLevelAssignmentsMap(
    levels: MatrixNestLevel[],
): Record<string, ServiceLevelAssignments> {
    const map: Record<string, ServiceLevelAssignments> = {};
    for (const level of levels) {
        if (!level?.id) continue;
        const raw = (level as PMServiceLevel).serviceLevelAssignments;
        map[level.id] = parseServiceLevelAssignments(raw);
    }
    return map;
}

/**
 * Effective matrix quantity for a part on the selected levels.
 * Matches Service Matrix cells: an explicit assignment on a level replaces
 * nested inheritance for that level (`showInherited = inherited && !hasOwn`).
 * Across selected levels, the highest per-level effective qty wins.
 */
export function getEffectiveMatrixQuantityForPart(
    partId: string,
    selectedLevels: PMServiceLevel[],
    allLevels: PMServiceLevel[] = selectedLevels,
): number {
    if (!partId || selectedLevels.length === 0) return 0;
    const nestPool = allLevels.length > 0 ? allLevels : selectedLevels;
    const assignmentsByLevel = buildServiceLevelAssignmentsMap(nestPool);

    let maxQty = 0;
    for (const level of selectedLevels) {
        const explicit = getAssignmentQuantity(assignmentsByLevel[level.id]?.[partId]);
        // Explicit replaces inherited on this level (same as matrix UI).
        if (explicit > 0) {
            maxQty = Math.max(maxQty, explicit);
            continue;
        }
        const inherited = getInheritedMatrixAssignment(
            level,
            partId,
            nestPool,
            assignmentsByLevel,
        );
        if (inherited) maxQty = Math.max(maxQty, inherited.quantity);
    }
    return maxQty;
}

/**
 * Resolves parts assigned across multiple service levels into a deduplicated union.
 * For each level, parses its `serviceLevelAssignments` JSON, collects assigned part IDs,
 * deduplicates, and returns matching parts preserving original order.
 *
 * When `allLevels` is provided, also includes parts inherited onto a selected level
 * via evenly divisible lower intervals (Service Matrix nesting).
 *
 * Validates: Requirements 2.5, 3.2
 */
export function getPartsForMultipleServiceLevels(
    parts: PMPartsAndProcedure[],
    serviceLevels: PMServiceLevel[],
    allLevels?: PMServiceLevel[],
): PMPartsAndProcedure[] {
    const nestPool = allLevels && allLevels.length > 0 ? allLevels : serviceLevels;
    const assignedPartIds = new Set<string>();

    for (const level of serviceLevels) {
        const assignments = parseServiceLevelAssignments(level.serviceLevelAssignments);
        for (const [partId, isAssigned] of Object.entries(assignments)) {
            if (isAssignedToServiceLevel(isAssigned)) {
                assignedPartIds.add(partId);
            }
        }
    }

    if (allLevels && allLevels.length > 0) {
        for (const part of parts) {
            if (!part?.id || assignedPartIds.has(part.id)) continue;
            if (getEffectiveMatrixQuantityForPart(part.id, serviceLevels, nestPool) > 0) {
                assignedPartIds.add(part.id);
            }
        }
    }

    return parts.filter((part) => assignedPartIds.has(part.id));
}

// ----- Submission Validation (Req 9.7, 9.12) -----

/**
 * Represents the state of a PM inspection submission for validation.
 */
export interface PMSubmissionState {
    /** Map of partId to completion status (true = completed). */
    completedItems: Record<string, boolean>;
    /** Whether a digital signature has been captured. */
    hasSignature: boolean;
    /** Actual UM at time of service. Required so the next due point can move. */
    actualUM?: string | number | null;
}

export interface PMValidationResult {
    isValid: boolean;
    errors: string[];
}

function hasActualServiceUM(value: string | number | null | undefined): boolean {
    if (typeof value === 'number') return Number.isFinite(value) && value > 0;
    const parsed = Number.parseFloat(String(value ?? '').trim());
    return Number.isFinite(parsed) && parsed > 0;
}

/**
 * Validates a PM inspection submission.
 * - Signature is required.
 * - Actual UM at Time of Service is required and must be greater than zero.
 *   A blank reading saves the report and leaves the queue on the old due point.
 * - At least one matrix item must be marked done (prevents an empty sign-and-submit
 *   from advancing the schedule). Additional items may stay unchecked so those
 *   consumables are omitted from Cost Tracking.
 * - If the matrix has no items, the signature and Actual UM are still required.
 */
export function validatePMSubmission(formState: PMSubmissionState): PMValidationResult {
    const errors: string[] = [];

    if (!formState.hasSignature) {
        errors.push("Digital signature is required before submission");
    }

    if (!hasActualServiceUM(formState.actualUM)) {
        errors.push("Actual UM at Time of Service is required before submission");
    }

    const itemIds = Object.keys(formState.completedItems);
    if (itemIds.length > 0) {
        const anyCompleted = itemIds.some((id) => formState.completedItems[id]);
        if (!anyCompleted) {
            errors.push("Mark at least one part or procedure as completed before submission");
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
}
